import React, { useEffect, useMemo, useState } from 'react';

import { useParams } from 'react-router-dom';

import Column from '@amzn/meridian/column';
import Text from '@amzn/meridian/text';
import LoadingSpinner from 'src/components/shared/loadingSpinner/loadingSpinner';
import associateProfileService from 'src/services/associateProfile/associateProfileService';
import type { AssociateProfilePlacementsResponse } from 'src/services/associateProfile/types';
import TaskService from 'src/services/tasks/taskService';
import { useAppSelector } from 'src/store/store';
import type { DataFetchingState } from 'src/types';
import {
  regionTaskListToRestrictionItemTasks,
  type RestrictionListParentTask,
  type RestrictionListTask,
} from 'src/utils/restrictionUtils';

import ActivePlacementCard from './activePlacementCard';
import PendingPlacementCard from './pendingPlacementCard';
import TransferredInPlacementCard from './transferredInPlacementCard';

type PageState = DataFetchingState<{
  placements: AssociateProfilePlacementsResponse;
  regionTasks: (RestrictionListTask | RestrictionListParentTask)[];
}>;

const DEFAULT_PAGE_STATE: PageState = {
  status: 'loading',
} as const;

const AssociateProfilePlacements = () => {
  const { alias } = useParams();
  const { selectedSite } = useAppSelector((state) => state.sites);
  const [pageState, setPageState] = useState<PageState>(DEFAULT_PAGE_STATE);

  useEffect(() => {
    const fetchAllPageData = async () => {
      setPageState({ status: 'loading' });

      if (!alias || selectedSite.id === 0) {
        setPageState({ status: 'error' });
        return;
      }

      try {
        // Fetch placements first
        const { data: placements } = await associateProfileService.getAssociateProfilePlacements(
          alias,
          selectedSite.id,
        );

        // Get region ID for region tasks
        // const allowedSite = allowedSites.find((site) => site.id === selectedSite.id);
        const regionId = selectedSite?.regionId ?? -1;

        let regionTasks: (RestrictionListTask | RestrictionListParentTask)[] = [];

        if (regionId > -1) {
          const rawRegionTasks = await TaskService.fetchRegionTasksByRegionId(regionId);
          regionTasks = regionTaskListToRestrictionItemTasks(rawRegionTasks);
        }

        setPageState({
          status: 'success',
          data: { placements, regionTasks },
        });
      } catch (error) {
        console.error('Failed to fetch page data:', error);
        setPageState({ status: 'error' });
      }
    };

    fetchAllPageData();
  }, [alias, selectedSite]);

  if (pageState.status === 'loading') {
    return <LoadingSpinner />;
  }

  if (pageState.status === 'error') {
    return <Text>No placements</Text>;
  }

  return (
    <Column spacing="400">
      <Text type="b500">Placements Pending Action ({pageState.data.placements.pending.length})</Text>
      {pageState.data.placements.pending.map((placement) => (
        <PendingPlacementCard
          key={placement.accommodationId}
          placement={placement}
          regionTasks={pageState.data.regionTasks}
        />
      ))}
      {pageState.data.placements.transferredIn.map((placement) => (
        <TransferredInPlacementCard key={placement.accommodationId} placement={placement} />
      ))}
      <Text type="b500">Active Placements ({pageState.data.placements.active.length})</Text>
      {pageState.data.placements.active.map((placement) => (
        <ActivePlacementCard
          key={placement.accommodationId}
          placement={placement}
          regionTasks={pageState.data.regionTasks}
        />
      ))}
    </Column>
  );
};

export default AssociateProfilePlacements;
